import { CircleAlert, Zap } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { RudderstackBrand } from '@/_shared/components/RudderstackBrand';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const ERROR_MESSAGES: Record<string, string> = {
  oauth_failed: 'GitHub login failed. Please try again.',
};

export function Login(): React.ReactElement {
  usePageTitle('Login');
  const [searchParams] = useSearchParams();
  const rawError = searchParams.get('error');
  const errorMessage = rawError ? (ERROR_MESSAGES[rawError] ?? rawError) : null;
  const returnTo = searchParams.get('return_to');
  const githubAuthUrl = returnTo ? `/auth/github?return_to=${encodeURIComponent(returnTo)}` : '/auth/github';
  const devLoginUrl = returnTo ? `/auth/dev/login?return_to=${encodeURIComponent(returnTo)}` : '/auth/dev/login';

  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,rgba(16,94,213,0.1),transparent_26%),linear-gradient(180deg,#f8fbff_0%,#f4f7fc_100%)] px-4">
      <div className="w-full max-w-sm animate-fade-in rounded-[1.75rem] border border-border/80 bg-white/92 p-8 text-center shadow-[0_28px_60px_-38px_rgba(47,66,112,0.38)] backdrop-blur-xl">
        <RudderstackBrand className="mb-5 justify-center" label="Rudder AI" badge="beta" />
        <p className="mb-8 text-sm text-muted-foreground">Powered by RudderStack</p>

        {errorMessage && (
          <div className="mb-6 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-left">
            <CircleAlert size={16} className="mt-0.5 shrink-0 text-destructive" />
            <div>
              <p className="text-sm text-destructive">{errorMessage}</p>
              {isLocalhost && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Try the Dev login button below for a fresh session.
                </p>
              )}
            </div>
          </div>
        )}

        <a
          href={githubAuthUrl}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border/90 bg-white/80 px-4 py-2.5 text-sm text-foreground transition-colors hover:border-brand hover:text-brand"
        >
          <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden="true">
            <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
          </svg>
          Sign in with GitHub
        </a>
        {isLocalhost && (
          <>
            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border/60" />
              <span>or</span>
              <div className="h-px flex-1 bg-border/60" />
            </div>
            <a
              href={devLoginUrl}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border/80 bg-muted/30 px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:border-brand hover:text-brand"
            >
              <Zap size={16} />
              Dev login
            </a>
            <p className="mt-2 text-xs text-muted-foreground">Bypasses OAuth — local dev only</p>
          </>
        )}
        <p className="mt-6 text-xs text-muted-foreground">Authenticate via your organization GitHub account</p>
      </div>
    </div>
  );
}
